import React, { useRef, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { 
  createAccessibleTabAttributes,
  createAccessibleTabPanelAttributes,
  createKeyboardNavigationHandler
} from '../utils/consolidatedAccessibility.js';
import { getTouchFriendlyClasses } from '../utils/responsiveUtils.js';
import { getFocusClasses } from '../utils/consolidatedAccessibility.js';

const ConversationTabs = ({ 
  tabs, 
  activeTab, 
  onTabChange, 
  className = '',
  'aria-label': ariaLabel = 'Conversation status tabs'
}) => {
  const { theme } = useTheme();
  const layoutClasses = {
    tabs: 'w-full',
    tabsList: 'flex border-b border-gray-200 dark:border-gray-700',
    tab: 'px-4 py-2 text-sm font-medium border-b-2 border-transparent hover:border-gray-300 dark:hover:border-gray-600 focus:outline-none focus:border-teal-500',
    tabActive: 'text-teal-600 dark:text-teal-400 border-teal-500 dark:border-teal-400',
    tabInactive: 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
  };
  const touchClasses = getTouchFriendlyClasses();
  const tabRefs = useRef({});
  
  // Focus management for keyboard navigation
  useEffect(() => {
    if (tabRefs.current[activeTab]) {
      tabRefs.current[activeTab].focus();
    }
  }, [activeTab]);
  
  const handleKeyDown = (event, tabId) => {
    const currentIndex = tabs.findIndex(tab => tab.id === tabId);
    let nextIndex;
    
    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        nextIndex = currentIndex > 0 ? currentIndex - 1 : tabs.length - 1;
        onTabChange(tabs[nextIndex].id);
        break;
      case 'ArrowRight':
        event.preventDefault();
        nextIndex = currentIndex < tabs.length - 1 ? currentIndex + 1 : 0;
        onTabChange(tabs[nextIndex].id);
        break;
      case 'Home':
        event.preventDefault();
        onTabChange(tabs[0].id);
        break;
      case 'End':
        event.preventDefault();
        onTabChange(tabs[tabs.length - 1].id);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        onTabChange(tabId);
        break;
    }
  };

  return (
    <div className={`${layoutClasses.tabs} ${className}`}>
      <nav 
        className={layoutClasses.tabsList}
        role="tablist"
        aria-label={ariaLabel}
      >
        {tabs.map((tab, index) => {
          const isSelected = activeTab === tab.id;
          const tabId = `conversation-tab-${tab.id}`;
          const panelId = `conversation-panel-${tab.id}`;
          const focusClasses = getFocusClasses(theme, 'primary');
          
          return (
            <button
              key={tab.id}
              ref={el => tabRefs.current[tab.id] = el}
              onClick={() => onTabChange(tab.id)}
              onKeyDown={(e) => handleKeyDown(e, tab.id)}
              className={`
                ${layoutClasses.tab}
                ${touchClasses.tab}
                ${focusClasses}
                ${isSelected ? layoutClasses.tabActive : layoutClasses.tabInactive}
                transition-colors duration-200
                flex items-center space-x-2
              `}
              {...createAccessibleTabAttributes(tabId, panelId, isSelected)}
            >
              <span className="flex items-center space-x-2">
                <span>{tab.label}</span>
                {typeof tab.count === 'number' && (
                  <span 
                    className={`
                      inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium min-w-[1.5rem] justify-center
                      ${isSelected
                        ? 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200'
                        : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                      }
                    `}
                    aria-label={`${tab.count} ${tab.label.toLowerCase()} conversations`}
                  >
                    {tab.count}
                  </span>
                )}
              </span>
              
              {/* Screen reader only content */}
              <span className="sr-only">
                {isSelected ? '(current tab)' : ''}
                {typeof tab.count === 'number' ? `, ${tab.count} items` : ''}
              </span>
            </button>
          );
        })}
      </nav>
      
      {/* Instructions for screen readers */}
      <div className="sr-only" aria-live="polite">
        Use arrow keys to navigate between tabs, Enter or Space to select a tab.
        Currently viewing {tabs.find(tab => tab.id === activeTab)?.label || activeTab} tab.
      </div>
    </div>
  );
};

export default ConversationTabs;
